import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AlertCircle, Mail, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailVerificationBanner = () => {
  const { t } = useTranslation();
  const [emailVerified, setEmailVerified] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    checkVerificationStatus();
  }, []);

  const checkVerificationStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/auth/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEmailVerified(response.data.email_verified);
      setUserEmail(response.data.email || '');
    } catch (error) {
      // Silently fail - don't show errors for verification status check
      console.log('Verification status check failed (silent):', error.response?.status);
      setEmailVerified(true); // Default to verified to hide banner on error
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResending(true);
      const token = localStorage.getItem('auth_token');
      
      await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('emailVerificationBanner.verificationSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setResending(false);
    }
  };

  if (loading || emailVerified) {
    return null;
  }

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-full">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-amber-800">
              {t('emailVerificationBanner.verifyEmail')}
            </p>
            <p className="text-xs text-amber-600">
              {t('emailVerificationBanner.checkInbox')} <span className="font-medium">{userEmail}</span> {t('emailVerificationBanner.forVerification')}
              {' '}{t('emailVerificationBanner.cannotCreate')}
            </p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleResendVerification}
          disabled={resending}
          className="border-amber-300 text-amber-700 hover:bg-amber-100"
        >
          {resending ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              {t('emailVerificationBanner.sending')}
            </>
          ) : (
            <>
              <Mail className="w-4 h-4 mr-2" />
              {t('emailVerificationBanner.resendEmail')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
